import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { MoContext } from 'contexts/MoContext';
import { actionTargetsOrder, useOrdersAction } from '../Assistant/ordersActions';
import { MyOrder, OrderRow } from 'ts/shared/interfaces/MOResponse';
import Button from 'components/ui/Button/Button';
import FormGroupSelect from 'components/ui/FormGroupSelect/FormGroupSelect';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import useModalClass from 'hooks/useModalClass';
import useRecaptcha from 'hooks/useRecaptcha';
import { getStatus } from 'constants/mo';
import { sanitizeString } from 'utils/shared/utils';
import { addRemark } from 'services/myOrders';
import { showError, showSuccess } from 'utils/utils';
import useDraggableModal from 'hooks/useDraggableModal';

interface MoAddRemarkProps {
  orderRow: OrderRow;
  onSuccess: (res: MyOrder) => void;
}

const MoAddRemark: FC<MoAddRemarkProps> = ({ orderRow, onSuccess }) => {
  const [t] = useTranslation();
  const { activeOrder } = useContext(MoContext);
  const isActive =
    activeOrder?.orderRow.segmentId === orderRow.segmentId ||
    activeOrder?.fullSegment.orderSegId === orderRow.segmentId;
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const { handleRecapcha } = useRecaptcha();
  const [remarkCode, setRemarkCode] = useState('APPMAN');
  const [notes, setNotes] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (openModal) {
      setIsSubmitted(false);
      setNotes('');
    }
  }, [openModal]);

  const remarkCodes = [
    { label: 'Response Manual (APPMAN)', value: 'APPMAN' },
    { label: 'Approval Phone (APPYES)', value: 'APPYES' },
    { label: 'Segment Alert (APPART)', value: 'APPART' },
    { label: 'Segment Open Task (APPOPN)', value: 'APPOPN' },
  ];

  const submitRemark = async (notesValue: string, codeValue: string) => {
    if (!notesValue) return;

    setOpenModal(false);
    setLoading(true);
    const token = await handleRecapcha();

    if (!token) {
      setLoading(false);
      return;
    }

    const params = {
      segmentId: orderRow.segmentId,
      orderId: orderRow.orderId,
      recaptchaToken: token,
      notes: notesValue,
      remarkCode: codeValue,
    };

    const response = await addRemark(params);
    if (response && !response?.hasErrors) {
      showSuccess(t('mo.remark_added'));
      onSuccess(response.myOrder);
    } else if (response?.hasErrors) {
      showError(response?.errorMessage);
    }

    setLoading(false);
  };

  const onClickConfirm = async () => {
    setIsSubmitted(true);
    if (!notes) return;
    await submitRemark(notes, remarkCode);
  };

  useOrdersAction(
    (action) =>
      action.action === 'add-remark' &&
      actionTargetsOrder(action, { segmentId: orderRow.segmentId, orderId: orderRow.orderId }, isActive),
    (action) => {
      if (action.remarkCode) setRemarkCode(action.remarkCode);
      if (action.message) {
        setNotes(action.message);
        void submitRemark(action.message, action.remarkCode || remarkCode);
        return;
      }
      setOpenModal(true);
    },
    isActive,
  );

  return (
    <Fragment>
      <Button
        onClick={() => setOpenModal(true)}
        title={t(loading ? 'mo.loading' : 'mo.add_remark')}
        className="btn btn-primary btn-ssm mb-10"
        disabled={loading}
        icon={faPen}
        iconclassname="mr-5"
      />

      {openModal && (
        <div
          className="modal-overlay modal-overlay-top modal-maximizable" style={{ direction: 'ltr' }}
          ref={modalOverlayRef}
          onClick={(e) => e.target === e.currentTarget && setOpenModal(false)}
        >
          <div ref={modalRef} className={classList} style={modalStyle}>
            <div
              className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
              {...dragHandleProps}
            >
              <div>

                <span className="title mr-10"> {t('mo.add_remark')}  <span className="mr-5">{' '}-{' '}Order: {orderRow.orderId}</span>
                  <span className="mr-5">Segment: {orderRow.segmentId}</span>
                  {!!orderRow.status && (
                    <span className={`mo-status ${getStatus(orderRow.status)}`}>
                      {orderRow.status}
                    </span>
                  )}</span>

              </div>
              <div data-no-drag="true">
                <ScreenshotSupport />
                <MaximizeModal modalOverlayRef={modalOverlayRef} />
                <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
              </div>
            </div>
            <div className="modal-content pt-10">
              <FormGroupSelect
                options={remarkCodes}
                value={remarkCode}
                onChange={setRemarkCode}
                label={t('mo.remark_code')}
              />
              <div
                className={'mt-20 form-group mb-30' + (!notes && isSubmitted ? ' is-invalid' : '')}
              >
                <div className="text-sm mb-5">{t('notes')}</div>
                <textarea
                  className={'form-control text-sm' + (!notes && isSubmitted ? '' : '')}
                  onChange={(e) => setNotes(sanitizeString(e.target.value))}
                  style={{ height: '140px', resize: 'none' }}
                  value={notes}
                />
                {!notes && isSubmitted && (
                  <div className="form-invalid-text">{t('validation.required')}</div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <Button
                className="btn btn-primary ml-0 mr-a"
                title={t('confirm')}
                disabled={loading}
                onClick={() => onClickConfirm()}
              />
              <Button
                className="btn btn-default"
                title={t('close')}
                onClick={() => setOpenModal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default MoAddRemark;
